import { ContextMenu } from "@/components/ui";
import { MatterRelatedMatterContextMenu } from "@/components/features/matter/context-menus/MatterRelatedMatterContextMenu";
import {
  MatterSectionCard,
  MatterSectionCardLineItem,
} from "@/components/features/matter/layout/MatterSectionCard/MatterSectionCard";
import { StatuteConfirmedIcon } from "@/components/ui/Icons/StatuteConfirmedIcon";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { getPrimaryMatterStatute } from "@/lib/data/static/matters/statuteStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type Matter } from "@/lib/types";
import { formatAddress, formatDate, formatTime } from "@/lib/utils/format";

export const MatterIncidentDetails = ({
  matter,
  noBorder = false,
}: {
  matter: Matter;
  /** Omit inner section border when wrapped in an outer bordered card (e.g. client tab). */
  noBorder?: boolean;
}) => {
  const { hasActionPermission } = useUser();
  const { openCaseIncidentDescriptionModal } = useGlobalModal();
  const canEditIncidentDetails = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const primaryStatute = getPrimaryMatterStatute(matter);
  const formattedIncidentAddress = formatAddress({
    street1: matter.incidentAddressStreet1,
    street2: matter.incidentAddressStreet2,
    city: matter.incidentAddressCity,
    state: matter.incidentAddressState ?? "",
    country: matter.incidentAddressCountry,
    zipCode: matter.incidentAddressZip,
  });
  const incidentLocationDetails = matter.incidentAddressDetails?.trim() ?? "";
  const details = (
    <MatterSectionCard
      title="Incident details"
      hasUpdatePermission={canEditIncidentDetails}
      onUpdateClick={() => {
        openCaseIncidentDescriptionModal({
          matterId: matter.id,
        });
      }}
      noHeaderBackground
      noBorder={noBorder}
    >
      <MatterSectionCardLineItem
        title="Date of loss"
        value={formatDate(matter.dateOfLoss) ?? "-"}
      />
      <MatterSectionCardLineItem
        title="Incident time"
        value={formatTime(matter.incidentTime ?? "") ?? "-"}
      />
      <MatterSectionCardLineItem
        title="Statute of limitations"
        value={
          <div className="flex items-center gap-x-2">
            {formatDate(primaryStatute?.date) ?? "-"}
            {primaryStatute ? (
              <StatuteConfirmedIcon confirmed={primaryStatute.confirmed} />
            ) : null}
          </div>
        }
      />
      <MatterSectionCardLineItem
        title="Client minor at incident"
        value={
          matter.clientMinorAtIncident === true
            ? "Yes"
            : matter.clientMinorAtIncident === false
              ? "No"
              : "Not specified"
        }
      />
      <MatterSectionCardLineItem
        title="Death occurred from incident"
        value={
          matter.deathOccurredFromIncident === true
            ? "Yes"
            : matter.deathOccurredFromIncident === false
              ? "No"
              : "Not specified"
        }
      />
      <MatterSectionCardLineItem
        title="Government entity involved"
        value={
          matter.governmentEntityInvolved === true
            ? "Yes"
            : matter.governmentEntityInvolved === false
              ? "No"
              : "Not specified"
        }
      />
      <MatterSectionCardLineItem
        title="Incident description"
        valueClassName="whitespace-pre-wrap"
        value={matter.incidentDescription ?? "-"}
      />
      <MatterSectionCardLineItem
        title="Incident address"
        value={formattedIncidentAddress || "-"}
      />
      {matter.incidentAddressCounty && (
        <MatterSectionCardLineItem
          title="Incident county"
          value={matter.incidentAddressCounty ?? "-"}
        />
      )}
      {incidentLocationDetails && (
        <MatterSectionCardLineItem
          title="Incident location details"
          valueClassName="whitespace-pre-wrap"
          value={incidentLocationDetails}
        />
      )}
    </MatterSectionCard>
  );
  if (!matter.incident) return details;
  return (
    <ContextMenu
      entityName="incident"
      trigger={<div>{details}</div>}
      open={
        canEditIncidentDetails
          ? {
              onSelect: () =>
                openCaseIncidentDescriptionModal({ matterId: matter.id }),
            }
          : false
      }
    >
      <MatterRelatedMatterContextMenu
        matter={matter}
        objectId={matter.incident.id}
        objectType="incident"
      />
    </ContextMenu>
  );
};
